import React from 'react';
import { TagsInput } from 'src/design/atoms/TagsInput';
import { useContactsByCustomer } from 'src/api/queries/organization/useContactsByCustomer';
import {
  RecipientDto as NotificationRecipientDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import * as Yup from 'yup';

const emailSchema = Yup.string().email().required();

export interface IRecipientPickerProps {
  label?: string;
  placeholder?: string;
  value: NotificationRecipientDto[];
  onChange: (value: NotificationRecipientDto[]) => void;
  jobUsers: UserDto[];
  customerNumber: string;
}

/** @registry Workflow notification recipient picker: free-text email TagsInput with suggestions from job users and client contacts; value is a list of notification recipients (free-text entries become address-only). */
export const RecipientPicker: React.FC<IRecipientPickerProps> = ({
  label,
  placeholder,
  value,
  onChange,
  jobUsers,
  customerNumber,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const { data: contacts } = useContactsByCustomer(customerNumber);

  const knownByAddress = React.useMemo(() => {
    const map = new Map<string, NotificationRecipientDto>();
    jobUsers.forEach((user) => {
      if (user.email) {
        map.set(user.email.toLowerCase(), {
          address: user.email,
          displayName: user.fullName,
          userId: user.id,
        });
      }
    });
    (contacts ?? []).forEach((contact) => {
      if (contact.email) {
        map.set(contact.email.toLowerCase(), {
          address: contact.email,
          displayName: `${contact.firstName ?? ''} ${
            contact.lastName ?? ''
          }`.trim(),
          contactId: contact.id,
        });
      }
    });
    return map;
  }, [jobUsers, contacts]);

  const suggestions = React.useMemo(() => {
    return Array.from(
      new Set(Array.from(knownByAddress.values()).map((r) => r.address)),
    );
  }, [knownByAddress]);

  const handleChange = (addresses: string[]) => {
    const existing = new Set(value.map((r) => r.address.toLowerCase()));
    const accepted: string[] = [];
    let invalid: string | null = null;
    addresses.forEach((address) => {
      const lower = address.toLowerCase();
      const isValid =
        existing.has(lower) ||
        knownByAddress.has(lower) ||
        emailSchema.isValidSync(address);
      if (isValid) {
        accepted.push(address);
      } else if (!invalid) {
        invalid = address;
      }
    });

    setError(invalid ? `Invalid email address: "${invalid}"` : null);

    const next = accepted.map((address) => {
      const known = knownByAddress.get(address.toLowerCase());
      return known ?? { address };
    });
    onChange(next);
  };

  return (
    <TagsInput
      label={label}
      placeholder={placeholder}
      data={suggestions}
      value={value.map((r) => r.address)}
      onChange={handleChange}
      error={error ?? undefined}
      onSearchChange={() => {
        if (error) setError(null);
      }}
    />
  );
};
